/**
 * Active coaching org context for the live product API.
 *
 * Every coaching product route requires a positive-integer `orgId` query
 * parameter (`parseOrgId` in the Coaching API shell helpers); the value is
 * resolved from the secret-free `/auth/me` membership projection after login
 * and injected into live product requests by the service layer. The persisted
 * hint is origin-scoped so fresh tabs/pages in one authenticated browser
 * context do not repeat the `/auth/me` -> product-list waterfall; auth failures
 * and logout clear it before it can survive a signed-out session.
 */

const ORG_ID_STORAGE_KEY = "vjal-coaching:active-org-id";
const ROLE_STORAGE_KEY = "vjal-coaching:active-role";
const ROLE_HINTS = new Set(["super_admin", "admin", "coach", "company_admin", "cohort_lead", "participant", "member"]);

let activeOrgId: string | null = readStoredOrgId();
let activeRole: string | null = readStoredRole();

export function setCoachingOrgId(orgId: string | null | undefined) {
  activeOrgId = isPositiveIntegerOrgId(orgId) ? orgId : null;
  writeStoredOrgId(activeOrgId);
  if (!activeOrgId) {
    activeRole = null;
    writeStoredRole(null);
  }
}

export function setCoachingProfileHint(orgId: string | null | undefined, role: string | null | undefined) {
  activeOrgId = isPositiveIntegerOrgId(orgId) ? orgId : null;
  activeRole = activeOrgId && isCoachingRoleHint(role) ? role : null;
  writeStoredOrgId(activeOrgId);
  writeStoredRole(activeRole);
}

export function getCoachingOrgId(): string | null {
  return activeOrgId;
}

export function getCoachingOrgIdHint(): string | null {
  activeOrgId ??= readStoredOrgId();
  return activeOrgId;
}

export function getCoachingRoleHint(): string | null {
  activeRole ??= readStoredRole();
  return activeRole;
}

export function clearCoachingOrgId() {
  activeOrgId = null;
  activeRole = null;
  writeStoredOrgId(null);
  writeStoredRole(null);
}

export function isPositiveIntegerOrgId(value: string | null | undefined): value is string {
  return typeof value === "string" && /^[1-9][0-9]*$/.test(value);
}

function isCoachingRoleHint(value: string | null | undefined): value is string {
  return typeof value === "string" && ROLE_HINTS.has(value);
}

function readStoredOrgId(): string | null {
  try {
    const value = typeof window === "undefined" ? null : window.localStorage.getItem(ORG_ID_STORAGE_KEY);
    return isPositiveIntegerOrgId(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredOrgId(orgId: string | null) {
  try {
    if (typeof window === "undefined") return;
    if (orgId) {
      window.localStorage.setItem(ORG_ID_STORAGE_KEY, orgId);
    } else {
      window.localStorage.removeItem(ORG_ID_STORAGE_KEY);
    }
  } catch {
    // Storage access is an optimization only; the in-memory org id remains authoritative.
  }
}

function readStoredRole(): string | null {
  try {
    const value = typeof window === "undefined" ? null : window.localStorage.getItem(ROLE_STORAGE_KEY);
    return isCoachingRoleHint(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredRole(role: string | null) {
  try {
    if (typeof window === "undefined") return;
    if (role) {
      window.localStorage.setItem(ROLE_STORAGE_KEY, role);
    } else {
      window.localStorage.removeItem(ROLE_STORAGE_KEY);
    }
  } catch {
    // Storage access is an optimization only; live profile hydration remains authoritative.
  }
}
